/**
 * 统一缓存管理器
 * 整合所有分散的缓存实现，提供统一的缓存接口
 */

/* eslint-disable max-classes-per-file */
import logger from '@/utils/logger';

// 缓存策略枚举
export enum CacheStrategy {
  LRU = 'lru',
  TTL = 'ttl',
  MEMORY = 'memory',
  SESSION = 'session',
  LOCAL = 'local',
}

// 缓存配置接口
export interface CacheConfig {
  strategy: CacheStrategy;
  maxSize?: number;
  ttl?: number; // 生存时间（毫秒）
  namespace?: string;
}

// 缓存项接口
interface CacheItem<T> {
  value: T;
  timestamp: number;
  ttl?: number;
  accessCount?: number;
  lastAccess?: number;
}

// 缓存统计接口
export interface CacheStats {
  size: number;
  maxSize: number;
  hitCount: number;
  missCount: number;
  hitRate: number;
  strategy: CacheStrategy;
  namespace?: string;
}

// 基础缓存接口
export interface ICache<T = any> {
  get(key: string): T | null;
  set(key: string, value: T, ttl?: number): void;
  has(key: string): boolean;
  delete(key: string): boolean;
  clear(): void;
  size(): number;
  keys(): string[];
  getStats(): CacheStats;
}

/**
 * LRU缓存实现
 */
class LRUCache<T> implements ICache<T> {
  private cache = new Map<string, CacheItem<T>>();

  private maxSize: number;

  private hitCount = 0;

  private missCount = 0;

  private namespace?: string;

  constructor(config: CacheConfig) {
    this.maxSize = config.maxSize || 100;
    this.namespace = config.namespace;
  }

  get(key: string): T | null {
    const item = this.cache.get(key);
    if (!item) {
      this.missCount++;
      return null;
    }

    // 检查TTL
    if (item.ttl && Date.now() - item.timestamp > item.ttl) {
      this.cache.delete(key);
      this.missCount++;
      return null;
    }

    // 更新访问信息（LRU）
    item.lastAccess = Date.now();
    item.accessCount = (item.accessCount || 0) + 1;

    // 移到最后（最近使用）
    this.cache.delete(key);
    this.cache.set(key, item);

    this.hitCount++;
    return item.value;
  }

  set(key: string, value: T, ttl?: number): void {
    // 如果已存在，先删除
    if (this.cache.has(key)) {
      this.cache.delete(key);
    }

    // 检查容量限制
    if (this.cache.size >= this.maxSize) {
      // 删除最久未使用的项（第一个）
      const firstKey = this.cache.keys().next().value;
      if (firstKey) {
        this.cache.delete(firstKey);
        logger.debug(`[CacheManager] LRU淘汰: ${firstKey}`);
      }
    }

    const item: CacheItem<T> = {
      value,
      timestamp: Date.now(),
      ttl,
      accessCount: 0,
      lastAccess: Date.now(),
    };

    this.cache.set(key, item);
  }

  has(key: string): boolean {
    const item = this.cache.get(key);
    if (!item) return false;

    // 检查TTL
    if (item.ttl && Date.now() - item.timestamp > item.ttl) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  delete(key: string): boolean {
    return this.cache.delete(key);
  }

  clear(): void {
    this.cache.clear();
    this.hitCount = 0;
    this.missCount = 0;
    logger.debug(`[CacheManager] LRU缓存已清空: ${this.namespace || 'default'}`);
  }

  size(): number {
    return this.cache.size;
  }

  keys(): string[] {
    return Array.from(this.cache.keys());
  }

  getStats(): CacheStats {
    const total = this.hitCount + this.missCount;
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      hitCount: this.hitCount,
      missCount: this.missCount,
      hitRate: total > 0 ? this.hitCount / total : 0,
      strategy: CacheStrategy.LRU,
      namespace: this.namespace,
    };
  }
}

/**
 * 内存缓存实现
 */
class MemoryCache<T> implements ICache<T> {
  private cache = new Map<string, CacheItem<T>>();

  private hitCount = 0;

  private missCount = 0;

  private namespace?: string;

  private maxSize: number;

  constructor(config: CacheConfig) {
    this.maxSize = config.maxSize || 1000;
    this.namespace = config.namespace;
  }

  get(key: string): T | null {
    const item = this.cache.get(key);
    if (!item) {
      this.missCount++;
      return null;
    }

    // 检查TTL
    if (item.ttl && Date.now() - item.timestamp > item.ttl) {
      this.cache.delete(key);
      this.missCount++;
      return null;
    }

    this.hitCount++;
    return item.value;
  }

  set(key: string, value: T, ttl?: number): void {
    if (this.cache.size >= this.maxSize && !this.cache.has(key)) {
      logger.warn(`[CacheManager] 内存缓存已满: ${this.namespace || 'default'}`);
      return;
    }

    const item: CacheItem<T> = {
      value,
      timestamp: Date.now(),
      ttl,
    };

    this.cache.set(key, item);
  }

  has(key: string): boolean {
    const item = this.cache.get(key);
    if (!item) return false;

    if (item.ttl && Date.now() - item.timestamp > item.ttl) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  delete(key: string): boolean {
    return this.cache.delete(key);
  }

  clear(): void {
    this.cache.clear();
    this.hitCount = 0;
    this.missCount = 0;
    logger.debug(`[CacheManager] 内存缓存已清空: ${this.namespace || 'default'}`);
  }

  size(): number {
    return this.cache.size;
  }

  keys(): string[] {
    return Array.from(this.cache.keys());
  }

  getStats(): CacheStats {
    const total = this.hitCount + this.missCount;
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      hitCount: this.hitCount,
      missCount: this.missCount,
      hitRate: total > 0 ? this.hitCount / total : 0,
      strategy: CacheStrategy.MEMORY,
      namespace: this.namespace,
    };
  }
}

/**
 * SessionStorage缓存实现
 */
class SessionCache<T> implements ICache<T> {
  private namespace: string;

  private hitCount = 0;

  private missCount = 0;

  constructor(config: CacheConfig) {
    this.namespace = config.namespace || 'default';
  }

  private getStorageKey(key: string): string {
    return `cache:${this.namespace}:${key}`;
  }

  get(key: string): T | null {
    try {
      const storageKey = this.getStorageKey(key);
      const itemStr = sessionStorage.getItem(storageKey);
      if (!itemStr) {
        this.missCount++;
        return null;
      }

      const item: CacheItem<T> = JSON.parse(itemStr);

      // 检查TTL
      if (item.ttl && Date.now() - item.timestamp > item.ttl) {
        sessionStorage.removeItem(storageKey);
        this.missCount++;
        return null;
      }

      this.hitCount++;
      return item.value;
    } catch (error) {
      logger.error('[CacheManager] SessionStorage读取失败:', error);
      this.missCount++;
      return null;
    }
  }

  set(key: string, value: T, ttl?: number): void {
    try {
      const item: CacheItem<T> = {
        value,
        timestamp: Date.now(),
        ttl,
      };

      const storageKey = this.getStorageKey(key);
      sessionStorage.setItem(storageKey, JSON.stringify(item));
    } catch (error) {
      logger.error('[CacheManager] SessionStorage写入失败:', error);
    }
  }

  has(key: string): boolean {
    return this.get(key) !== null;
  }

  delete(key: string): boolean {
    try {
      const storageKey = this.getStorageKey(key);
      sessionStorage.removeItem(storageKey);
      return true;
    } catch (error) {
      logger.error('[CacheManager] SessionStorage删除失败:', error);
      return false;
    }
  }

  clear(): void {
    try {
      const keysToRemove: string[] = [];
      const prefix = `cache:${this.namespace}:`;

      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(prefix)) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => sessionStorage.removeItem(key));
      this.hitCount = 0;
      this.missCount = 0;

      logger.debug(`[CacheManager] SessionStorage缓存已清空: ${this.namespace}`);
    } catch (error) {
      logger.error('[CacheManager] SessionStorage清空失败:', error);
    }
  }

  size(): number {
    try {
      let count = 0;
      const prefix = `cache:${this.namespace}:`;

      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(prefix)) {
          count++;
        }
      }

      return count;
    } catch (error) {
      logger.error('[CacheManager] SessionStorage大小计算失败:', error);
      return 0;
    }
  }

  keys(): string[] {
    try {
      const keys: string[] = [];
      const prefix = `cache:${this.namespace}:`;

      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(prefix)) {
          keys.push(key.substring(prefix.length));
        }
      }

      return keys;
    } catch (error) {
      logger.error('[CacheManager] SessionStorage键列表获取失败:', error);
      return [];
    }
  }

  getStats(): CacheStats {
    const total = this.hitCount + this.missCount;
    return {
      size: this.size(),
      maxSize: -1, // SessionStorage没有固定限制
      hitCount: this.hitCount,
      missCount: this.missCount,
      hitRate: total > 0 ? this.hitCount / total : 0,
      strategy: CacheStrategy.SESSION,
      namespace: this.namespace,
    };
  }
}

/**
 * 统一缓存管理器
 */
export class CacheManager {
  private caches = new Map<string, ICache>();

  private defaultConfig: CacheConfig = {
    strategy: CacheStrategy.LRU,
    maxSize: 100,
    ttl: 5 * 60 * 1000, // 5分钟
  };

  /**
   * 创建或获取缓存实例
   */
  getCache<T = any>(namespace: string, config?: Partial<CacheConfig>): ICache<T> {
    if (this.caches.has(namespace)) {
      return this.caches.get(namespace)! as ICache<T>;
    }

    const finalConfig: CacheConfig = {
      ...this.defaultConfig,
      ...config,
      namespace,
    };

    let cache: ICache<T>;

    switch (finalConfig.strategy) {
      case CacheStrategy.LRU:
        cache = new LRUCache<T>(finalConfig);
        break;
      case CacheStrategy.MEMORY:
        cache = new MemoryCache<T>(finalConfig);
        break;
      case CacheStrategy.SESSION:
        cache = new SessionCache<T>(finalConfig);
        break;
      default:
        cache = new LRUCache<T>(finalConfig);
    }

    this.caches.set(namespace, cache);
    logger.debug(`[CacheManager] 创建缓存实例: ${namespace} (${finalConfig.strategy})`);

    return cache;
  }

  /**
   * 删除缓存实例
   */
  removeCache(namespace: string): boolean {
    const cache = this.caches.get(namespace);
    if (cache) {
      cache.clear();
      this.caches.delete(namespace);
      logger.debug(`[CacheManager] 删除缓存实例: ${namespace}`);
      return true;
    }
    return false;
  }

  /**
   * 清空所有缓存
   */
  clearAll(): void {
    this.caches.forEach((cache, namespace) => {
      cache.clear();
      logger.debug(`[CacheManager] 清空缓存: ${namespace}`);
    });
  }

  /**
   * 获取所有缓存统计
   */
  getAllStats(): Record<string, CacheStats> {
    const stats: Record<string, CacheStats> = {};
    this.caches.forEach((cache, namespace) => {
      stats[namespace] = cache.getStats();
    });
    return stats;
  }

  /**
   * 获取缓存实例列表
   */
  getCacheNames(): string[] {
    return Array.from(this.caches.keys());
  }

  /**
   * 销毁管理器
   */
  destroy(): void {
    this.clearAll();
    this.caches.clear();
    logger.info('[CacheManager] 缓存管理器已销毁');
  }
}

// 导出单例实例
export const cacheManager = new CacheManager();

// 导出便捷函数
export const createCache = <T = any>(namespace: string, config?: Partial<CacheConfig>) =>
  cacheManager.getCache<T>(namespace, config);

export const getCache = <T = any>(namespace: string) => cacheManager.getCache<T>(namespace);

export const clearCache = (namespace: string) => {
  const cache = cacheManager.getCache(namespace);
  cache.clear();
};

export const getCacheStats = (namespace: string) => {
  const cache = cacheManager.getCache(namespace);
  return cache.getStats();
};
